import {
  BookOpenIcon,
  ChatIcon,
  CursorClickIcon,
  DocumentTextIcon,
  GiftIcon,
  IdentificationIcon,
  QuestionMarkCircleIcon,
  ReceiptRefundIcon,
  SearchIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  TagIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import { useLanguagesFaq } from '../sharedFaqs';
import type { IndustryContent } from '../types';
import ShopifyConnect from '../visuals/ShopifyConnect';
import { tone } from '../../primitives';

const BRAND_BG = 'bg-[#8a4b2a]';

export function useEcommerceContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');
  const languagesFaq = useLanguagesFaq();

  return {
    key: 'ecommerce',
    seoTitle: t('E-commerce Chatbot: AI Shopping Assistant for Your Store'),
    seoDescription: t(
      'Help shoppers find the right product and buy with confidence. An AI agent that searches your Shopify catalog, tracks orders and answers shipping and returns questions.',
    ),
    hero: {
      title: t('A shop assistant on every page.'),
      accent: t('One that knows your whole catalog.'),
      lead: t(
        'Connect your Shopify store and your agent finds the right products with prices and links, answers sizing, shipping and returns questions, and tells customers where their order is.',
      ),
      description: t(
        'Example: a shopper asks a footwear store whether boots fit a wide foot. The agent searches the catalog, suggests two wide-fit styles and answers a shipping question.',
      ),
      surface: 'widget',
      brand: {
        name: 'Fernwood Boots',
        initial: 'F',
        kind: t('Online store'),
        bg: BRAND_BG,
      },
      messages: [
        {
          from: 'visitor',
          text: tc('Will these boots fit a wide foot?'),
          at: 600,
        },
        {
          from: 'agent',
          typingAt: 1100,
          at: 1900,
          content: tc(
            'Most customers with wide feet go half a size up. Here are our wide-fit styles.',
          ),
        },
        { from: 'agent', at: 2500, content: <ProductCards /> },
        {
          from: 'visitor',
          text: t('Great. Do you ship to Ireland?'),
          at: 3500,
        },
        {
          from: 'agent',
          typingAt: 3900,
          at: 4700,
          content: t(
            'Yes, in 2 to 4 working days, and returns are free for 30 days.',
          ),
        },
      ],
      steps: [
        {
          icon: SearchIcon,
          title: t('Searched the catalog'),
          via: 'Shopify',
          at: 1400,
        },
        {
          icon: ShoppingBagIcon,
          title: t('Found 2 wide-fit styles'),
          via: 'Shopify',
          at: 2200,
        },
        {
          icon: BookOpenIcon,
          title: t('Checked the shipping policy'),
          via: tc('Knowledge'),
          at: 4100,
        },
      ],
    },
    jobs: {
      title: t('Every question between browsing and buying.'),
      tail: t('Answered on the spot.'),
      lead: t(
        'Shoppers leave when they can’t find an answer. Your agent knows your catalog and your policies, so it answers straight away and points them to the right product.',
      ),
      items: [
        {
          icon: SearchIcon,
          title: t('Product finder'),
          detail: t(
            'Searches your Shopify catalog and suggests matching products with prices and links.',
          ),
        },
        {
          icon: TagIcon,
          title: t('Sizing and fit'),
          detail: t(
            'Answers fit questions from your size guides and product details.',
          ),
        },
        {
          icon: TruckIcon,
          title: t('Order tracking'),
          detail: t(
            'Shares an order’s status once the customer gives the order number and checkout email.',
          ),
        },
        {
          icon: ReceiptRefundIcon,
          title: t('Shipping and returns'),
          detail: t(
            'Explains delivery times, costs and your returns policy in plain words.',
          ),
        },
        {
          icon: GiftIcon,
          title: t('Gift ideas'),
          detail: t(
            'Suggests products by budget, style or occasion from your catalog.',
          ),
        },
        {
          icon: ShoppingCartIcon,
          title: t('Back to checkout'),
          detail: t(
            'Shows a button to the product, the cart or an offer when the shopper is ready.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Plugged into your store.'),
      tail: t('Set up in an afternoon.'),
      learns: [
        { icon: 'FaShopify', label: t('Shopify catalog') },
        { icon: DocumentTextIcon, label: t('Shipping and returns policies') },
        { icon: TagIcon, label: t('Size guides') },
        { icon: QuestionMarkCircleIcon, label: t('FAQ page') },
      ],
      acts: [
        { icon: SearchIcon, label: t('Product search') },
        { icon: TruckIcon, label: t('Order status') },
        { icon: CursorClickIcon, label: tc('Buttons') },
        { icon: IdentificationIcon, label: tc('Collect data') },
      ],
      channels: [
        { icon: ChatIcon, label: t('Chat on your store') },
        { icon: 'FaWhatsapp', label: 'WhatsApp' },
        { icon: 'FaFacebookMessenger', label: 'Messenger' },
        { icon: 'FaEnvelope', label: tc('Email') },
        { icon: 'FaTelegram', label: 'Telegram' },
      ],
    },
    spotlight: {
      title: t('Connect Shopify in minutes.'),
      tail: t('No app to install.'),
      lead: t(
        'Create a private app in your own Shopify account, paste its keys and add one line to your theme. You can revoke access at any time.',
      ),
      points: [
        t('Finds products with prices and links'),
        t('Shares order status only when the order number and email match'),
        t('Works in the chat on your store and on your shareable link'),
        t('Available on paid plans'),
      ],
      description: t(
        'Example: the Shopify connection window with the store connected and the chat code ready to paste into the theme, next to an order status shared after the customer’s details matched.',
      ),
      visual: <ShopifyConnect brand={BRAND_BG} />,
    },
    faqs: [
      {
        question: t('Does it work with Shopify?'),
        answer: t(
          'Yes. Connect your store and the agent searches your products, with prices and links, and looks up orders. You create a private app in your own Shopify account, so nothing is installed from the app store.',
        ),
      },
      {
        question: t('Can it tell customers where their order is?'),
        answer: t(
          'Yes, once the customer gives the order number and the email used at checkout. It shares the status only when both match.',
        ),
      },
      {
        question: t('I don’t use Shopify. Can I still use it?'),
        answer: t(
          'Yes. Add the chat to any site with one line of code and train it on your product pages and policies. A custom action can connect it to your own order system.',
        ),
      },
      {
        question: t('Can it handle returns?'),
        answer: t(
          'It explains your returns policy and links to your returns page or form. The return itself is processed in your store as usual.',
        ),
      },
      languagesFaq,
    ],
    finalExchange: {
      question: t('Do these boots run true to size?'),
      answer: t('They run a little small, so most people go half a size up.'),
    },
    finalTitle: t(
      'Your shop assistant could be helping customers by lunchtime.',
    ),
  };
}

/** Two products the agent found, as cards in the chat. */
function ProductCards() {
  const { t } = useLocale('bot-use-cases');
  const products = [
    {
      name: 'Highland Chelsea',
      price: '€189',
      swatch: 'bg-[linear-gradient(140deg,#c98a5e,#7a4325)]',
    },
    {
      name: 'Trail Lace-up',
      price: '€165',
      swatch: 'bg-[linear-gradient(140deg,#8d8f6a,#4c4d33)]',
    },
  ];

  return (
    <div className='grid w-[88%] grid-cols-2 gap-2'>
      {products.map(({ name, price, swatch }) => (
        <div
          key={name}
          className='overflow-hidden rounded-xl bg-white ring-1 ring-black/[0.07] dark:bg-white/[0.05] dark:ring-white/10'
        >
          <div
            className={clsxm('flex h-16 items-center justify-center', swatch)}
          >
            <svg viewBox='0 0 64 40' className='h-9 w-14' aria-hidden='true'>
              <path
                d='M12 4h16v16c0 3 3 5 7 6l13 3c4 1 6 3 6 6v1H10a2 2 0 0 1-2-2V8a4 4 0 0 1 4-4z'
                fill='rgba(255,255,255,0.88)'
              />
              <path d='M8 33h48' stroke='rgba(0,0,0,0.35)' strokeWidth='3' />
            </svg>
          </div>
          <div className='px-2.5 py-2'>
            <p
              className={clsxm('truncate text-[12px] font-semibold', tone.ink)}
            >
              {name}
            </p>
            <p className='mt-0.5 flex items-center justify-between gap-1 text-[11px]'>
              <span className={clsxm('truncate', tone.quiet)}>
                {t('Wide fit')}
              </span>
              <span className={clsxm('font-semibold tabular-nums', tone.ink)}>
                {price}
              </span>
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
